import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Activity,
  Smartphone,
  Heart,
  Droplet,
  Wind,
  Scale,
  Download,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import ReactSelect from "react-select";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { useAuth } from "@/contexts/use-auth";
import PatientDevice from "./PatientDevice";
import BloodPressureReadings from "./readings/BloodPressureReadings";
import BloodGlucoseReadings from "./readings/BloodGlucoseReadings";
import BloodOxygenReadings from "./readings/BloodOxygenReadings";
import BodyCompositionReadings from "./readings/BodyCompositionReadings";
import PulseRateReadings from "./readings/PulseRateReadings";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  exportPatientDeviceTabXlsx,
  type DeviceTabExportKey,
} from "@/utils/exportPatientDeviceTabXlsx";
import moment from "moment";

type DateRangeYmd = { startDate: string; endDate: string };

interface PatientReadingsProps {
  patient: any;
  hideListTab?: boolean;
  isPractitioner?: boolean;
  /** When true (e.g. patient on /patient-device), show only tabs for devices assigned to this patient */
  showOnlyAssignedDeviceTabs?: boolean;
  /** Incremented from parent (e.g. after /reading/sync/all) to refetch readings + devices */
  readingsRefreshNonce?: number;
}

interface Reading {
  _id: string;
  deviceType: string;
  readingDate: string;
  readingDateLocal?: string;
  glucose?: number;
  glucoseUnit?: string;
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  spo2?: number;
  weight?: number;
  weightUnit?: string;
  status: string;
  isAbnormal: boolean;
  [key: string]: any;
}

type PatientReadingTab = {
  name: string;
  value: string;
  deviceType: string | null;
  isDeviceTab: boolean;
  icon: React.ComponentType<{ className?: string }>;
  isPulseRateTab?: boolean;
};

const tabConfig: PatientReadingTab[] = [
  { name: "List", value: "device", deviceType: null, isDeviceTab: true, icon: Smartphone },
  { name: "Blood Pressure", value: "blood_pressure", deviceType: "blood_pressure", isDeviceTab: false, icon: Heart },
  { name: "Blood Glucose", value: "blood_glucose", deviceType: "glucose_meter", isDeviceTab: false, icon: Droplet },
  { name: "Blood Oxygen", value: "pulse_oximeter", deviceType: "pulse_oximeter", isDeviceTab: false, icon: Wind },
  { name: "Weight Scale", value: "weight_scale", deviceType: "weight_scale", isDeviceTab: false, icon: Scale },
  // { name: "Pulse Rate", value: "pulse_rate", deviceType: null, isDeviceTab: false, isPulseRateTab: true, icon: HeartPulse },
];

const TAB_SWITCH_LOADER_SECONDS = 4;

const PatientReadings: React.FC<PatientReadingsProps> = ({
  patient,
  hideListTab,
  isPractitioner = false,
  showOnlyAssignedDeviceTabs,
  readingsRefreshNonce = 0,
}) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isCaretaker = user?.role === "caretaker";
  const [activeTab, setActiveTab] = useState("device");
  // console.log("activeTab", activeTab);
  const [tabSwitchLoader, setTabSwitchLoader] = useState(true); // show loader on first/default tab render
  const tabSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitialLoaderRun = useRef(false);
  const [isMapDeviceDialogOpen, setIsMapDeviceDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [deviceRefreshKey, setDeviceRefreshKey] = useState(0);
  const [deviceExportLoading, setDeviceExportLoading] = useState(false);
  const [initialTabsWithReadings, setInitialTabsWithReadings] = useState<string[]>([]);
  const [initialReadingsChecked, setInitialReadingsChecked] = useState(false);
  const patientId = patient?._id;
  const fetchReadingsRef = useRef<(() => void) | null>(null);
  const initialCheckPatientRef = useRef<string | null>(null);
  const [allDevices, setAllDevices] = useState<unknown[]>([]);
  // console.log("allDevices", allDevices);
  useEffect(() => {
    const fetchDevices = async () => {
    const { data: res } = await AxiosInstance().get<{
      data?: { devices?: unknown[] };
    }>("/devices", {
      params: { patientId, page: 1, limit: 10000, skip: 0 },
    });
      const devices = res?.data?.devices ?? [];
      setAllDevices(devices);
    };
    if (patientId) {
    fetchDevices();
    }
  }, [patientId]);


  const [tabDateRanges, setTabDateRanges] = useState<
    Partial<Record<string, DateRangeYmd>>
  >({});

  const handleReadingTabDateRangeChange = useCallback(
    (tabValue: string, range: DateRangeYmd) => {
      setTabDateRanges((p) => ({ ...p, [tabValue]: range }));
    },
    [],
  );

  /** Stable `(range) => void` per tab so reading components can safely depend on `onDateRangeChange`. */
  const readingTabDateRangeByTabValue = useMemo(() => {
    const map: Partial<Record<string, (range: DateRangeYmd) => void>> = {};
    for (const t of tabConfig) {
      if (t.isDeviceTab) continue;
      const tabValue = t.value;
      map[tabValue] = (range: DateRangeYmd) =>
        handleReadingTabDateRangeChange(tabValue, range);
    }
    return map;
  }, [handleReadingTabDateRangeChange]);

  const applyDateFilterForExport = useCallback(
    <T extends { readingDate?: string; readingDateLocal?: string }>(
      rows: T[],
      range?: DateRangeYmd,
    ) => {
      if (!range?.startDate || !range?.endDate) return rows;
      const rangeStart = moment(range.startDate, "YYYY-MM-DD").startOf("day");
      const rangeEnd = moment(range.endDate, "YYYY-MM-DD").endOf("day");
      return (rows || []).filter((r: any) => {
        const dt = r?.readingDateLocal || r?.readingDate;
        if (!dt) return false;
        const m = moment(dt);
        return m.isBetween(rangeStart, rangeEnd, undefined, "[]");
      });
    },
    [],
  );

  const handleTabChange = (value: string) => {
    const isReadingTab = !tabConfig.find((t) => t.value === value)?.isDeviceTab;
    if (isReadingTab && value !== activeTab) {
      if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current);
      setTabSwitchLoader(true);
      setActiveTab(value);
      tabSwitchTimerRef.current = setTimeout(() => {
        setTabSwitchLoader(false);
        tabSwitchTimerRef.current = null;
      }, TAB_SWITCH_LOADER_SECONDS * 1000);
    } else {
      setActiveTab(value);
    }
  };

  // 5 sec loader on first/default tab render
  useEffect(() => {
    if (hasInitialLoaderRun.current) return;
    hasInitialLoaderRun.current = true;
    const t = setTimeout(() => setTabSwitchLoader(false), TAB_SWITCH_LOADER_SECONDS * 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    return () => {
      if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current);
    };
  }, []);

  // Fetch patient's assigned devices (for filtering tabs when showOnlyAssignedDeviceTabs)
  const { apiData: patientDevicesData } = useGetData(
    "/devices",
    { patientId },
    false,
    !!patientId && !!showOnlyAssignedDeviceTabs,
    () => {}
  );

  const assignedDevices: any[] = patientDevicesData?.data?.devices || [];
  const assignedDeviceTypes = Array.from(
    new Set((assignedDevices || []).map((d: any) => d?.deviceType).filter(Boolean))
  ) as string[];

  useEffect(() => {
    if (!patientId) return;
    if (initialCheckPatientRef.current === patientId) return;

    initialCheckPatientRef.current = patientId;
    setInitialReadingsChecked(false);
    setInitialTabsWithReadings([]);

    const deviceTypesToCheck = Array.from(
      new Set(
        tabConfig
          .map((tab) => tab.deviceType)
          .filter((deviceType): deviceType is string => Boolean(deviceType))
      )
    );

    let isCancelled = false;

    const checkReadings = async () => {
      try {
        const results = await Promise.allSettled(
          deviceTypesToCheck.map((deviceType) =>
            AxiosInstance().get(`/devices/patient/${patientId}/readings`, {
              params: { deviceType , limit: 1  },
            })
          )
        );

        if (isCancelled) return;

        const tabsWithData: string[] = [];
        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const response = result.value as any;
          const readings = response?.data?.data?.readings || [];
          if (readings.length > 0) {
            tabsWithData.push(deviceTypesToCheck[index]);
          }
        });
        // console.log("tabsWithData", tabsWithData);
        setInitialTabsWithReadings(tabsWithData);
      } catch (error) {
        if (!isCancelled) {
          setInitialTabsWithReadings([]);
        }
      } finally {
        if (!isCancelled) {
          setInitialReadingsChecked(true);
        }
      }
    };

    checkReadings();

    return () => {
      isCancelled = true;
    };
  }, [patientId]);

  // Filter out "List" tab only for caretakers (patient's own device page shows List + other tabs)
  // When showOnlyAssignedDeviceTabs: show only List + tabs for device types the patient has assigned
  const visibleTabs = tabConfig.filter((tab) => {
    // if (tab.isDeviceTab && isCaretaker) {
    //   return false;
    // }
    if (tab.isDeviceTab) {
      return true;
    }
    if (tab.isPulseRateTab) {
      // Show Pulse Rate tab only when Blood Pressure tab would be visible.
      const bloodPressureTabVisible = showOnlyAssignedDeviceTabs
        ? assignedDeviceTypes.includes("blood_pressure") ||
          initialTabsWithReadings.includes("blood_pressure")
        : true;
      return bloodPressureTabVisible;
    }
    if (showOnlyAssignedDeviceTabs) {
      return tab.deviceType
        ? assignedDeviceTypes.includes(tab.deviceType) ||
            initialTabsWithReadings.includes(tab.deviceType)
        : false;
    }
  
    return true;
  });

  // Set default tab to first visible tab when List is hidden (caretaker only)
  useEffect(() => {
    if (isCaretaker && activeTab === "device") {
      const firstVisibleTab = visibleTabs[0]?.value;
      if (firstVisibleTab) {
        setActiveTab(firstVisibleTab);
      }
    }
  }, [isCaretaker]);

  // When showOnlyAssignedDeviceTabs: if current activeTab is not in visibleTabs, switch to first visible
  useEffect(() => {
    if (showOnlyAssignedDeviceTabs && visibleTabs.length > 0 && !visibleTabs.some((t) => t.value === activeTab)) {
      setActiveTab(visibleTabs[0].value);
    }
  }, [showOnlyAssignedDeviceTabs, visibleTabs, activeTab]);

  // Get current tab's device type
  const currentTab = tabConfig.find((tab) => tab.value === activeTab);
  const currentDeviceType = currentTab?.deviceType;
  const isReadingTab = currentDeviceType && !currentTab?.isDeviceTab && !currentTab?.isPulseRateTab;

  // Fetch readings data with deviceType query parameter for reading tabs
  const {
    apiData: readingsData,
    loading: readingsLoading1,
    err: readingsErr,
    fetchData: fetchReadings,
  } = useGetData(
    `/devices/patient/${patientId}/readings`,
    currentDeviceType ? { deviceType: currentDeviceType } : {},
    false,
    !!patientId && !!isReadingTab,
    () => { }
  );
  
  const readingsLoading = readingsLoading1;
  // Store fetchReadings in ref to avoid dependency issues
  fetchReadingsRef.current = fetchReadings;

  const readings: Reading[] = readingsData?.data?.readings || [];

  // Refresh readings after adding manually
  const refreshReadings = useCallback(() => {
    if (patientId && fetchReadingsRef.current) {
      fetchReadingsRef.current();
    }
  }, [patientId]);

  useEffect(() => {
    if (!readingsRefreshNonce) return;
    refreshReadings();
    setDeviceRefreshKey((k) => k + 1);
  }, [readingsRefreshNonce, refreshReadings]);

  // Fetch devices for mapping (available devices)
  const {
    apiData: devicesData,
    loading: devicesLoading,
    err: devicesErr,
    fetchData: fetchDevices,
  } = useGetData(
    "/devices",
    {},
    false,
    isMapDeviceDialogOpen, // Only fetch when dialog is open
    () => {}
  );

  const devices: any[] = devicesData?.data?.devices || [];

  // Convert devices to react-select options format
  const deviceOptions = devices.map((device) => ({
    value: device._id,
    label: device?.imei ? `${device.imei} (${device.deviceName})` : device.deviceName,
  }));

  const handleDownloadDeviceTab = async () => {
    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }
    setDeviceExportLoading(true);
    try {
      const tabMeta = tabConfig.find((t) => t.value === activeTab);
      const tabLabel = tabMeta?.name || activeTab;
      const selectedRange = tabDateRanges[activeTab];

      if (activeTab === "device") {
        const { data: res } = await AxiosInstance().get<{
          data?: { devices?: unknown[] };
        }>("/devices", {
          params: { patientId, page: 1, limit: 10000, skip: 0 },
        });
        const devices = res?.data?.devices ?? [];
        exportPatientDeviceTabXlsx(
          patient,
          "device",
          tabLabel,
          devices,
          [],
          patientId,
          activeTab,
        );
      } else if (activeTab === "pulse_rate") {
        const { data: res } = await AxiosInstance().get<{
          data?: { readings?: unknown[] } | unknown[];
        }>("/reading/pulse", {
          params: { patientId },
        });
        const inner = res?.data;
        const readingsRaw = Array.isArray(inner)
          ? inner
          : inner && typeof inner === "object" && "readings" in inner
            ? (inner as { readings?: unknown[] }).readings ?? []
            : [];
        const readings = applyDateFilterForExport(readingsRaw as any[], selectedRange);
        exportPatientDeviceTabXlsx(
          patient,
          "pulse_rate",
          tabLabel,
          allDevices,
          readings,
          patientId,
          activeTab,
        );
      } else if (tabMeta?.deviceType) {
        const { data: res } = await AxiosInstance().get<{
          data?: { readings?: unknown[] };
        }>(`/devices/patient/${patientId}/readings`, {
          params: {
            deviceType: tabMeta.deviceType,
            limit: 10000,
            page: 1,
            skip: 0,
          },
        });
        const readingsRaw = (res?.data?.readings ?? []) as any[];
        console.log("readingsRaw", readingsRaw);
        const readings = applyDateFilterForExport(readingsRaw, selectedRange);
        console.log("readings", readings);
        exportPatientDeviceTabXlsx(
          patient,
          activeTab as DeviceTabExportKey,
          tabLabel,
          allDevices,
          readings,
          patientId,
          activeTab,
        );
      } else {
        toast.error("Nothing to export for this tab");
        return;
      }
      toast.success("Downloaded");
    } catch {
      toast.error("Could not export. Try again.");
    } finally {
      setDeviceExportLoading(false);
    }
  };

  const handleMapDevice = async () => {
    if (!selectedDeviceId) {
      toast.error("Please select a device");
      return;
    }

    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      const { data } = await AxiosInstance().post("/devices/map", {
        deviceId: selectedDeviceId,
        patientId: patientId,
      });

      resolveFn("Device mapped successfully");
      setIsMapDeviceDialogOpen(false);
      setSelectedDeviceId("");
      // Refresh patient devices list by updating key
      setDeviceRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to map device"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="border-grey border-[0.3px] rounded-[5px]">
      {/* <div className="flex items-center justify-between p-4">
        <h2 className="flex items-center gap-2 teachers font-bold text-xl">
          <Activity className="w-5 h-5" />
          Readings
        </h2>
      </div>
      <Separator className="bg-grey" /> */}
      <div className="p-4">
        {/* <h2 className="flex items-center gap-2 teachers font-bold text-xl mb-4">
          List
        </h2> */}











        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="mb-2 flex flex-col gap-2 md:mb-4 md:flex-row md:items-center md:justify-between md:overflow-x-auto">
            <TabsList className="h-10 w-full shrink-0 flex-wrap rounded-lg border border-grey-light bg-grey-light p-0 md:w-auto">
              {visibleTabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="teachers rounded-lg text-sm px-4 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none  flex items-center gap-2"
                  >
                    {IconComponent && <IconComponent className="w-4 h-4" />}
                    {!isMobile && tab.name}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {activeTab !== "device" && (
              <Button
                type="button"
                variant="outline"
                className="shrink-0 self-start md:self-center"
                disabled={deviceExportLoading || !patientId}
                onClick={handleDownloadDeviceTab}
              >
                <Download className="mr-2 h-4 w-4" />
                {deviceExportLoading ? "Exporting…" : "XLSX"}
              </Button>
            )}
          </div>
          {visibleTabs.map((tab) => {
            // If it's the Device tab, show device component
            if (tab.isDeviceTab) {
              return (
                <TabsContent key={tab.value} value={tab.value} className="mt-4">
                  <LoaderWrapper loading={tabSwitchLoader} error={null}>
                    <PatientDevice key={deviceRefreshKey} patientId={patientId as string} hideMapButton={true} />
                  </LoaderWrapper>
                </TabsContent>
              );
            }

            // Handle Pulse Rate tab separately (uses different API)
            if (tab.isPulseRateTab) {
              return (
                <TabsContent key={tab.value} value={tab.value} className="mt-4">
                  <LoaderWrapper loading={tabSwitchLoader} error={null}>
                    <PulseRateReadings
                      patientId={patientId as string}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  </LoaderWrapper>
                </TabsContent>
              );
            }

            // Filter readings for this specific tab
            const tabReadings = readings.filter(
              (r) => r.deviceType === tab.deviceType
            );
            const tabDeviceType = tab.deviceType;

            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-0 md:mt-4">
                <LoaderWrapper loading={tabSwitchLoader || readingsLoading} error={readingsErr}>
                  {tabDeviceType === "blood_pressure" && (
                    <BloodPressureReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      onReadingAdded={refreshReadings}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "glucose_meter" && (
                    <BloodGlucoseReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      onReadingAdded={refreshReadings}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "pulse_oximeter" && (
                    <BloodOxygenReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      onReadingAdded={refreshReadings}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "weight_scale" && (
                    <BodyCompositionReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      onReadingAdded={refreshReadings}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                </LoaderWrapper>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Map Device Dialog */}
        <Dialog
          open={isMapDeviceDialogOpen}
          onOpenChange={(open) => {
            setIsMapDeviceDialogOpen(open);
            if (!open) {
              setSelectedDeviceId("");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Map Device</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <LoaderWrapper loading={devicesLoading} error={devicesErr}>
                <div className="space-y-2">
                  <Label htmlFor="device-select">
                    Select Device <span className="text-destructive">*</span>
                  </Label>
                  <ReactSelect
                    id="device-select"
                    options={deviceOptions}
                    value={deviceOptions.find((opt) => opt.value === selectedDeviceId) || null}
                    onChange={(selected) => {
                      setSelectedDeviceId(selected?.value || "");
                    }}
                    placeholder="Select a device"
                    isSearchable={true}
                    isClearable={false}
                    className="w-full"
                    classNamePrefix="device-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        borderColor: "#e5e7eb",
                        "&:hover": {
                          borderColor: "#d1d5db",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    noOptionsMessage={() => "No devices available"}
                  />
                </div>
              </LoaderWrapper>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsMapDeviceDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={btnLoading || !selectedDeviceId}
                  onClick={handleMapDevice}
                >
                  Map
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PatientReadings;





